import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { MotionValue, m } from 'framer-motion';

const ScrollBar = ({
  zIndex = 102,
  y,
  mountTo,
  offsetY = 0,
  delay = 300,
}: {
  zIndex?: number;
  y?: MotionValue<number>;
  mountTo?: HTMLElement;
  offsetY?: number;
  delay?: number;
}) => {
  const TRACK_HEIGHT = 80;
  const ref = useRef<HTMLDivElement>(null);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState({ height: 0, top: 0 });
  const [isVisible, setIsVisible] = useState(false);

  const onScroll = useCallback(() => {
    if (!containerRef) return;
    const { scrollHeight, clientHeight, scrollTop } = containerRef;
    const thumbHeight = (clientHeight / scrollHeight) * TRACK_HEIGHT;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = TRACK_HEIGHT - thumbHeight;

    const thumbTop = (scrollTop / maxScrollTop) * maxThumbTop;

    setThumb({ height: thumbHeight, top: thumbTop });

    const iconContainer = document.querySelector('[data-component="ProfileImage"] .icon-group-cont') as HTMLElement;
    if (iconContainer) {
      iconContainer.style.insetInlineEnd = thumbHeight === TRACK_HEIGHT ? '0' : '1rem';
    }
    
  }, [containerRef]);

  // transition 완료 후 렌더링
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isVisible || !ref.current) return;
    const container = ref.current.closest('.scroll-container');
    if (!container) return;
    setContainerRef(container as HTMLDivElement);
  }, [isVisible]);

  useEffect(() => {
    if (containerRef) {
      onScroll();
    }
  }, [containerRef, onScroll]);

  useEffect(() => {
    if (!containerRef) return;

    onScroll();

    containerRef.addEventListener('scroll', onScroll);

    return () => {
      containerRef.removeEventListener('scroll', onScroll);
    };
  }, [containerRef, onScroll]);

  const content = (
    <m.div
      ref={ref}
      data-component="ScrollBar"
      className="fixed h-[80px] w-2 rounded-full"
      style={{
        direction: BResource.getSelectedLocale() === 'ar' ? 'rtl' : 'ltr',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex,
        y,
        top: `calc(${offsetY}px + 1rem)`,
        insetInlineEnd: '1rem',
      }}
    >
      <div className="absolute left-0 right-0 top-0 rounded-full bg-white" style={{ height: `${thumb.height}px`, top: `${thumb.top}px` }} />
    </m.div>
  );

  if (!isVisible) return null;

  if (thumb.height === TRACK_HEIGHT) return null;

  if (containerRef) {
    return createPortal(content, mountTo ?? document.getElementById('hMainContainer') ?? document.body);
  }

  return content;
};

export default ScrollBar;